import React, { useState, useEffect, useContext } from "react";
import PreferenceSwitcher from "../../component/PreferenceSwitcher";
import { Badge, Typography, List, Button, Drawer } from "antd";
import {
  IconAlignRight,
  IconBell,
  IconAddressBook,
  IconWallet,
  IconTruckDelivery,
  IconReceipt2,
  IconAlertCircle
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import "../../css/topNav.css";
import GlobalProfileMenu from "../../util/GlobalProfileMenu";
import handleLogout from "../../util/logout";
import { useGlobalAuthModal } from "../../util/GlobalAuthModal";
import { usePreference } from "../../context/PreferenceContext";
import { ThemeContext } from "../../util/ThemeContext";
import near from "../../assets/img/near.jpg";
import UserServ from '../../service/UserServ';
import { fromNow } from '../../util/dateUtils';
import { jwtDecode } from 'jwt-decode';
import NotificationServ from '../../service/NotificationServ';
import { message } from 'antd';

const { Text } = Typography;

const TopNavSeller = ({ onToggle }) => {
  const { formatPrice, t } = usePreference();
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const { showAlert } = useGlobalAuthModal();
  const [stats, setStats] = useState({ messagesCount: 0, unreadNotifications: 0, firstName: "", lastName: "", credits: 0 });
  const [notifications, setNotifications] = useState([]);
  const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchStats = async () => {
    try {
      const res = await UserServ.getBuyerStats();
      const resSeller = await UserServ.getSellerStats();
      
      if (res && res.data) {
        setStats(prev => ({
          ...prev,
          ...res.data,
          unreadNotifications: resSeller?.data?.unreadNotifications || res.data.unreadNotifications
        }));
      }

      const notifRes = await UserServ.getNotifications({ limit: 5, role: 'seller' });
      if (notifRes && notifRes.data) {
        setNotifications(notifRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch seller stats", error);
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          setStats(prev => ({
            ...prev,
            firstName: decoded.firstName || decoded.name || decoded.fullName || ""
          }));
        } catch (e) { }
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setStats(prev => ({
          ...prev,
          firstName: decoded.firstName || decoded.name || decoded.fullName || ""
        }));
      } catch (e) { }
    }
    fetchStats();

    window.addEventListener('profile-updated', fetchStats);
    window.addEventListener('notification-refresh', fetchStats);
    return () => {
      window.removeEventListener('profile-updated', fetchStats);
      window.removeEventListener('notification-refresh', fetchStats);
    };
  }, []);

  const handleLogOut = () => {
    showAlert({
      title: t('logout_title'),
      description: t('logout_description'),
      buttons: [
        { text: t('stay_here'), variant: 'default' },
        {
          text: t('sign_out'),
          variant: 'primary',
          onClick: () => {
            handleLogout();
          }
        }
      ]
    });
  };

  const getIcon = (item) => {
    const type = item.type;
    const baseClass = "pcu-padding-8 pcu-radius-8 pcu-flex pcu-items-center pcu-justify-center";

    switch (type) {
      case 'order':
        return <div className={`${baseClass} pcu-bg-orange-light`}><IconTruckDelivery color="var(--nearzon-orange)" size={20} /></div>;
      case 'payment':
        return <div className={`${baseClass} pcu-bg-green-light`}><IconReceipt2 color="var(--nearzon-success)" size={20} /></div>;
      case 'system':
        return <div className={`${baseClass} pcu-bg-blue-light`}><IconAlertCircle color="var(--nearzon-blue)" size={20} /></div>;
      default:
        return <div className={`${baseClass} pcu-bg-gray-light`}><IconBell color="#64748b" size={20} /></div>;
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await UserServ.markAllNotificationsRead();
      fetchStats();
      window.dispatchEvent(new Event('notification-refresh'));
    } catch (error) {
      console.error("Failed to mark all as read", error);
    }
  };

  const handleNotificationClick = async (item) => {
      setNotifDrawerOpen(false);

      if (!item.isRead) {
          try {
              await NotificationServ.markAsRead(item._id);
              fetchStats();
              window.dispatchEvent(new Event("notification-refresh"));
          } catch (error) {
              console.error("Failed to mark as read", error);
          }
      }

      const lowerTitle = item.title?.toLowerCase() || "";
      if (item.orderId || lowerTitle.includes('order')) {
          navigate('/seller/orders', { state: { highlightOrderId: item.orderId } });
          return;
      }
      if (lowerTitle.includes('seller request') || lowerTitle.includes('seller application')) {
          navigate('/seller/dashboard');
          return;
      }
      if (lowerTitle.includes('credit') || lowerTitle.includes('wallet')) {
          navigate('/seller/dashboard');
          return;
      }
      if (item.type === 'coupon' || lowerTitle.includes('coupon')) {
          if (item.metaData?.couponCode) {
              navigator.clipboard.writeText(item.metaData.couponCode);
              message.success(`Coupon code ${item.metaData.couponCode} copied!`);
          }
          navigate('/seller/dashboard');
          return;
      }
      navigate('/seller/dashboard');
  };

  const notificationContent = (
    <div className={isMobile ? "pcu-padding-0" : "pcu-padding-x-4"}>
      <div className="pcu-flex pcu-justify-between pcu-items-center pcu-margin-bottom-16">
        <Text strong className="pcu-text-18">{t('notifications')}</Text>
        <div className="pcu-flex pcu-gap-12">
          <Button
            type="link"
            size="small"
            onClick={handleMarkAllRead}
            className="pcu-padding-x-0"
          >
            {t('mark_all_read')}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              navigate('/seller/notifications');
              setNotifDrawerOpen(false);
            }}
            className="pcu-padding-x-0"
          >
            {t('view_all')}
          </Button>
        </div>
      </div>
      <List
        itemLayout="horizontal"
        dataSource={notifications}
        renderItem={item => (
          <List.Item
            onClick={() => handleNotificationClick(item)}
            className={`pcu-padding-12 pcu-cursor-pointer pcu-border-bottom pcu-radius-8 pcu-margin-bottom-4`}
            style={{
              backgroundColor: !item.isRead 
                                ? ((item.title?.toLowerCase().includes('reject') || item.title?.toLowerCase().includes('fail')) ? '#fef2f2' : '#fff7ed') 
                                : 'transparent',
              transition: 'all 0.2s ease'
            }}
          >
            <List.Item.Meta
              avatar={getIcon(item)}
              title={<Text strong={!item.isRead} className="pcu-text-14">{item.title}</Text>}
              description={
                <div className="pcu-text-12">
                  <div 
                    className="pcu-text-muted pcu-margin-bottom-2" 
                    dangerouslySetInnerHTML={{ __html: item.description }} 
                  />
                  <Text type="secondary" className="pcu-text-11">{fromNow(item.createdAt)}</Text>
                </div>
              }
            />
          </List.Item>
        )}
      />
      {notifications.length === 0 && (
        <div className="pcu-text-center pcu-padding-y-40 pcu-text-muted">{t('no_notifications')}</div>
      )}
    </div>
  );

  return (
    <div className={`topnav ${darkMode ? "dark" : "light"}`}>
      <div className="topnav-left">
        <button onClick={onToggle} className="menu-btn">
          <IconAlignRight size={22} />
        </button>
        {!isMobile && (
          <img
            src={near}
            alt="Nearzon"
            className="logo-img pcu-margin-left-10 pcu-cursor-pointer"
            onClick={() => navigate("/")}
          />
        )}
      </div>

      <div className="topnav-right">
        <div className={`pcu-flex pcu-items-center pcu-margin-right-8 desktop-only-flex ${isMobile ? "pcu-gap-8" : "pcu-gap-16"}`}>
          <PreferenceSwitcher mode={darkMode ? "dark" : "light"} />
          <div className={`credits-display pcu-flex pcu-items-center pcu-gap-6 pcu-padding-x-12 pcu-h-32 pcu-radius-20 pcu-cursor-pointer pcu-white-space-nowrap pcu-border pcu-transition-all pcu-shadow-sm ${darkMode ? 'pcu-bg-white-5 pcu-border-white-10' : 'pcu-bg-orange-light pcu-border-orange-200'}`}
            style={{ borderRadius: "20px" }}
            onClick={() => navigate('/seller/dashboard')}>
            <IconWallet size={20} color="var(--nearzon-orange)" />
            <Text strong className={`pcu-text-14 pcu-font-700 ${darkMode ? 'pcu-text-title' : 'pcu-text-orange-900'}`} style={{ marginTop: "8px" }}>
              {formatPrice(stats.walletBalance || 0)}
            </Text>
          </div>
          <div className="nav-icon-badge" onClick={() => navigate('/buyer/contact')}>
            <Badge count={stats.messagesCount} showZero={false}>
              <IconAddressBook size={22} color="#64748b" className="pcu-cursor-pointer" />
            </Badge>
          </div>
          <div className="nav-icon-badge" onClick={() => setNotifDrawerOpen(true)}>
            <Badge
              count={stats.unreadNotifications}
              showZero={false}
              className="pcu-cursor-pointer"
            >
              <IconBell
                size={22}
                color="#64748b"
              />
            </Badge>
          </div>
        </div>
        <GlobalProfileMenu handleLogOut={handleLogOut} stats={stats} />
      </div>

      <Drawer
        title={null}
        placement="right"
        onClose={() => setNotifDrawerOpen(false)}
        open={notifDrawerOpen}
        width={isMobile ? '85%' : 380}
        bodyStyle={{ padding: '24px' }}
      >
        {notificationContent}
      </Drawer>
    </div>
  );
};

export default TopNavSeller;
